import { Injectable, signal } from '@angular/core';

type ClarityModule = typeof import('@microsoft/clarity').default;

@Injectable({ providedIn: 'root' })
export class ClarityService {
  private clarityModule: ClarityModule | null = null;
  private initDone = false;

  /** Analytics consent granted and Clarity received `analytics_Storage: 'granted'`. */
  readonly analyticsAllowed = signal(false);

  /** True after `init()` has run (Clarity script / queue wired). */
  isInitialized(): boolean {
    return this.initDone;
  }

  private canSend(): boolean {
    return this.initDone && this.analyticsAllowed();
  }

  private async loadClarity(): Promise<ClarityModule> {
    if (!this.clarityModule) {
      const mod = await import('@microsoft/clarity');
      this.clarityModule = mod.default;
    }
    return this.clarityModule;
  }

  /**
   * CMP analytics consent maps to Clarity `analytics_Storage`; ads stay denied.
   * `init()` must run before any `consentV2` / identify / tags (npm package wires `window.clarity` in `init`).
   */
  async syncStatisticsConsent(projectId: string, statisticsConsent: boolean): Promise<void> {
    const Clarity = await this.loadClarity();

    if (!statisticsConsent) {
      this.analyticsAllowed.set(false);
      if (this.initDone) {
        Clarity.consentV2({ ad_Storage: 'denied', analytics_Storage: 'denied' });
      }
      return;
    }

    if (!this.initDone) {
      Clarity.init(projectId);
      this.initDone = true;
    }

    Clarity.consentV2({ ad_Storage: 'denied', analytics_Storage: 'granted' });
    this.analyticsAllowed.set(true);
  }

  async identify(
    customId: string,
    customSessionId?: string,
    customPageId?: string,
    friendlyName?: string,
  ): Promise<void> {
    if (!this.canSend()) {
      return;
    }
    const Clarity = await this.loadClarity();
    Clarity.identify(customId, customSessionId, customPageId, friendlyName);
  }

  async setTag(key: string, value: string | string[]): Promise<void> {
    if (!this.canSend()) {
      return;
    }
    const Clarity = await this.loadClarity();
    Clarity.setTag(key, value);
  }

  async event(eventName: string): Promise<void> {
    if (!this.canSend()) {
      return;
    }
    const Clarity = await this.loadClarity();
    Clarity.event(eventName);
  }

  async upgrade(reason: string): Promise<void> {
    if (!this.canSend()) {
      return;
    }
    const Clarity = await this.loadClarity();
    Clarity.upgrade(reason);
  }
}
